import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";

export const PENDING_SUB = ["past_due", "unpaid", "incomplete"];
export const renewalPending = (u) => !!u && PENDING_SUB.includes(u.subscription?.status);

export const useRenew = () => {
  const { tErr } = useLang();
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const renew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/subscription/renew");
      window.location.href = data.url;
      return true;
    } catch (e) { toast.error(tErr(errMsg(e))); refresh(); setBusy(false); return false; }
  };
  return { renew, busy };
};

export const RenewButton = ({ className = "", testId = "renew-now-button" }) => {
  const { t } = useLang();
  const { renew, busy } = useRenew();
  return (
    <Button onClick={renew} disabled={busy} className={`rounded-full bg-[#E40046] hover:bg-[#c3003c] ${className}`} data-testid={testId}>
      {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("renew.cta")}
    </Button>
  );
};

export const RenewalAlert = () => {
  const { user } = useAuth();
  const { t } = useLang();
  if (!renewalPending(user)) return null;
  return (
    <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
      <div data-testid="renewal-alert" className="flex flex-wrap items-center gap-4 rounded-xl border border-red-200 bg-red-50 p-4">
        <AlertTriangle className="h-5 w-5 shrink-0 text-red-500" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-900">{t("renew.title")}</p>
          <p className="text-xs text-slate-600">{t("renew.text")}</p>
        </div>
        <RenewButton />
      </div>
    </div>
  );
};

export default function RenewPage() {
  const { t, href } = useLang();
  const { renew } = useRenew();
  const navigate = useNavigate();
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    renew().then((ok) => { if (!ok) navigate(href("pricing"), { replace: true }); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="grid min-h-[50vh] place-items-center" data-testid="renew-page">
      <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />{t("renew.redirecting")}</p>
    </div>
  );
}
